import React, { useState } from 'react';
import {
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardSubtitle,
  IonCardContent,
  IonFooter,
  IonButton,
  IonRow,
  IonCol,
  IonSkeletonText,
  IonCheckbox,
  IonModal,
  IonLoading
} from '@ionic/react';

import './AppointmentCard.scss';

import { TestResult } from '../models';
import QRCode from 'react-qr-code';
import TestResultRepository from '../repositories/TestResultRepository';
import { SCAN_RESULT_ROUTE } from '../navigators/AppStackNavigator';

interface Props {
  result?: TestResult;
  skeleton?: boolean;
  onBeforeDelete?: Function;
  onDelete?: Function;
  deletable?: boolean;
  selectable?: boolean;
  selected?: boolean;
  opaque?: boolean;
  onSelect?: (result: TestResult) => void;
}

const ResultsCard: React.FC<Props> = ({
  result,
  skeleton = false,
  deletable = true,
  selectable = false,
  selected = false,
  opaque = false,
  onSelect = () => {}
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showQrCodeModal, setShowQrCodeModal] = useState(false);

  async function getPDF() {
    if (result) {
      setIsLoading(true);
      await TestResultRepository.getTestResultPDF(result);
      setIsLoading(false);
    }
  }

  async function showQRCode() {
    if (result) {
      setIsLoading(true);

      const { key, expired_at } =
        await TestResultRepository.getTestResultKeyById(result.id);

      result.key = key;
      result.expired_at = expired_at;

      setIsLoading(false);
      setShowQrCodeModal(true);
    }
  }

  if (!result || skeleton) {
    return (
      <IonCard>
        <IonRow>
          <IonCol size="12" sizeMd="5">
            <IonCardHeader>
              <IonRow className="ion-justify-content-between">
                <IonCol>
                  <IonCardTitle>
                    <IonSkeletonText animated style={{ width: '50%' }} />
                  </IonCardTitle>
                  <IonCardSubtitle>
                    <IonSkeletonText animated style={{ width: '30%' }} />
                  </IonCardSubtitle>
                </IonCol>
              </IonRow>
            </IonCardHeader>
          </IonCol>
          <IonCol size="12" sizeMd="6">
            <IonCardContent className="ion-no-padding-top-on-mobile">
              <IonSkeletonText animated style={{ width: '88%' }} />
              <IonSkeletonText animated style={{ width: '70%' }} />
              <IonSkeletonText animated style={{ width: '60%' }} />
            </IonCardContent>
          </IonCol>
        </IonRow>
      </IonCard>
    );
  }

  return (
    <IonCard
      className={[
        opaque ? 'appointment-card--opaque' : '',
        selectable ? 'appointment-card--selectable' : '',
        selected ? 'appointment-card--selected' : ''
      ].join(' ')}
      onClick={() => onSelect(result)}
    >
      <IonRow>
        {selectable ? (
          <IonCol size="1">
            <IonCardContent>
              <IonCheckbox color="primary" checked={selected} />
            </IonCardContent>
          </IonCol>
        ) : null}
        <IonCol size={selectable ? '11' : '12'} sizeMd={selectable ? '5' : '6'}>
          <IonCardHeader>
            <IonRow className="ion-justify-content-between">
              <IonCol>
                <IonCardTitle>{result.getDayString()}</IonCardTitle>
                <IonCardSubtitle>{result.getHourString()}</IonCardSubtitle>
                <IonCardTitle className="ion-margin-top">
                  Type de test
                </IonCardTitle>
                <IonCardSubtitle>{result.type}</IonCardSubtitle>
              </IonCol>
            </IonRow>
          </IonCardHeader>
        </IonCol>
        <IonCol size="12" sizeMd="6">
          <IonCardContent className="ion-no-padding-top-on-mobile">
            <div className="ion-margin-bottom">
              <strong>Numéro de dossier</strong>
              <br />-
            </div>
            {result.laboratory?.address.split(',').map((line) => (
              <p key={line}>{line}</p>
            ))}
            <p>{result.patient?.getFullName()}</p>
          </IonCardContent>
        </IonCol>
      </IonRow>
      {deletable ? (
        <IonFooter className="appointment-card__footer">
          <IonRow className="ion-justify-content-between ion-align-items-center">
            <IonCol size="auto"></IonCol>
            <IonCol size="auto">
              <IonButton onClick={() => showQRCode()} fill="clear">
                Voir le QR code
              </IonButton>
              <IonButton onClick={() => getPDF()} download="" fill="clear">
                Voir les résultats
              </IonButton>
            </IonCol>
          </IonRow>
        </IonFooter>
      ) : null}
      <IonLoading isOpen={isLoading} />
      <IonModal
        isOpen={showQrCodeModal}
        onDidDismiss={() => setShowQrCodeModal(false)}
        swipeToClose={true}
      >
        <IonCard
          style={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            overflow: 'scroll'
          }}
        >
          <div className="ion-padding ion-text-center">
            <h1>Présentez le QR Code</h1>
          </div>
          <div style={{ flexShrink: 0 }}>
            <QRCode
              value={`${window.location.origin}${SCAN_RESULT_ROUTE}/${result.key}`}
            />
          </div>
          <IonButton
            onClick={() => setShowQrCodeModal(false)}
            className="ion-margin-top"
          >
            Fermer
          </IonButton>
        </IonCard>
      </IonModal>
    </IonCard>
  );
};

export default ResultsCard;
